import { useState } from 'react';
import { cn } from '@/components/ui/utils';

interface Props {
  onAddNote: (body: string, isBlocker: boolean) => Promise<{ error: unknown }>;
  onResolveBlocker?: (noteId: string) => Promise<{ error: unknown }>;
  resolvedNoteId?: string | null;  // if present, show "Mark resolved" CTA
  disabled?: boolean;
}

export function BlockerComposer({ onAddNote, onResolveBlocker, resolvedNoteId, disabled }: Props) {
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [confirm, setConfirm] = useState<string | null>(null);

  const handleAddNote = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    const { error } = await onAddNote(body.trim(), false);
    setSubmitting(false);
    if (!error) setBody('');
  };

  const handleFlagBlocker = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    const { error } = await onAddNote(body.trim(), true);
    setSubmitting(false);
    if (!error) {
      setBody('');
      setConfirm('Flagged. It\'ll get answered at the next review.');
      setTimeout(() => setConfirm(null), 4000);
    }
  };

  const handleResolve = async () => {
    if (!resolvedNoteId || !onResolveBlocker) return;
    setResolving(true);
    await onResolveBlocker(resolvedNoteId);
    setResolving(false);
  };

  return (
    <div className="space-y-3">
      {/* "Mark resolved" CTA — shown when task is blocked and an answer exists */}
      {resolvedNoteId && onResolveBlocker && (
        <div className="rounded-[4px] border border-[rgba(184,150,62,0.3)] bg-[rgba(184,150,62,0.08)] px-4 py-3 flex items-center justify-between gap-4">
          <p className="text-[14px] text-[var(--color-charcoal)]">
            New answer from Ken — ready to continue?
          </p>
          <button
            onClick={handleResolve}
            disabled={resolving}
            className={cn(
              'px-4 py-2 rounded-[4px] text-[14px] font-[500] transition-all duration-200',
              'bg-[var(--color-forest)] text-[var(--color-warm-white)]',
              'hover:bg-[var(--color-forest-light)] active:scale-95',
              resolving && 'opacity-60 cursor-not-allowed'
            )}
          >
            {resolving ? 'Marking…' : 'Mark resolved & continue'}
          </button>
        </div>
      )}

      {/* Composer */}
      <div className="space-y-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a note or describe what's blocking you…"
          disabled={disabled || submitting}
          rows={3}
          className={cn(
            'w-full rounded-[4px] border px-4 py-3 text-[15px] leading-relaxed resize-none',
            'border-[var(--color-charcoal)]/30 bg-white text-[var(--color-near-black)]',
            'placeholder:text-[var(--color-mid-gray)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-forest)] focus:ring-offset-0',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors duration-200'
          )}
        />

        <p className="text-[12px] text-[var(--color-mid-gray)]">
          Stuck or missing something? Flag it — it'll get answered at the next review.
        </p>

        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleAddNote}
            disabled={!body.trim() || submitting}
            className={cn(
              'px-4 py-2 rounded-[4px] text-[14px] font-[500] border transition-all duration-200',
              'border-[var(--color-forest)] text-[var(--color-forest)] bg-white',
              'hover:bg-[var(--color-forest)] hover:text-[var(--color-warm-white)]',
              'disabled:opacity-40 disabled:cursor-not-allowed active:scale-95'
            )}
          >
            Add note
          </button>

          <button
            onClick={handleFlagBlocker}
            disabled={!body.trim() || submitting}
            className={cn(
              'px-4 py-2 rounded-[4px] text-[14px] font-[500] border transition-all duration-200',
              'border-[var(--destructive)] text-[var(--destructive)] bg-white',
              'hover:bg-[var(--destructive)] hover:text-white',
              'disabled:opacity-40 disabled:cursor-not-allowed active:scale-95'
            )}
          >
            Flag as blocker
          </button>
        </div>

        {confirm && (
          <p className="text-[13px] font-[500] text-[var(--destructive)]" aria-live="polite">
            {confirm}
          </p>
        )}
      </div>
    </div>
  );
}
